/**
 * Fishionist AI Personalizer — front-end controller.
 *
 * Flow: upload photo -> Create my artwork (calls the Worker /generate) ->
 * preview the result -> Add to Cart.
 *
 * There is no separate "confirm" step. Add to Cart attaches the print URL on
 * its way through, and for a background product composes the print file first.
 *
 * Invariant that prevents shipping the wrong file:
 *   A line item cannot reach the cart unless _print_url was set from the
 *   artwork that was on screen at the moment the customer pressed Add to Cart.
 *   Any new generation or new upload clears it again.
 */
(function () {
  'use strict';

  const root = document.getElementById('fsh-personalizer');
  if (!root) return;

  const WORKER_URL = (root.dataset.workerUrl || '').replace(/\/+$/, '');
  const TURNSTILE_KEY = root.dataset.turnstileKey || '';
  const PRODUCT_ID = root.dataset.productId || '';
  const PRODUCT_HANDLE = root.dataset.productHandle || '';
  const GENERATE_TIMEOUT_MS = 120000;
  const MAX_UPLOAD_DIM = 1600; // downscale long edge before upload (speed + cost)

  // The one line item property the customer is meant to READ — see the long
  // note in assets/fsh-builder.js. Underscore names are hidden everywhere, so
  // without this the checkout page shows the product's stock photo and says
  // nothing about the poster attached to the order. Same string in
  // assets/fsh-builder.js and assets/artwork-drawer.js.
  const CART_NOTE_NAME = 'Your artwork';
  const CART_NOTE = 'Saved with this order. The image shown in checkout is a stock product photo';

  const els = {
    fileInput: root.querySelector('[id="fsh-file-input"]'),
    uploadCard: root.querySelector('[data-fsh-upload-card]'),
    uploadText: root.querySelector('[data-fsh-upload-text]'),
    generateBtn: root.querySelector('[data-fsh-generate]'),
    progress: root.querySelector('[data-fsh-progress]'),
    progressThumb: root.querySelector('[data-fsh-progress-thumb]'),
    progressSub: root.querySelector('[data-fsh-progress-sub]'),
    progressFill: root.querySelector('[data-fsh-progress-fill]'),
    progressPercent: root.querySelector('[data-fsh-progress-percent]'),
    preview: root.querySelector('[data-fsh-preview]'),
    stage: root.querySelector('[data-fsh-stage]'),
    artwork: root.querySelector('[data-fsh-artwork]'),
    bgstage: root.querySelector('[data-fsh-bgstage]'),
    bgstageBg: root.querySelector('[data-fsh-bgstage-bg]'),
    bgstageSubject: root.querySelector('[data-fsh-bgstage-subject]'),
    bgstageFrame: root.querySelector('[data-fsh-bgstage-frame]'),
    bgpicker: root.querySelector('[data-fsh-bgpicker]'),
    retryBtn: root.querySelector('[data-fsh-retry]'),
    error: root.querySelector('[data-fsh-error]'),
  };

  // State: the URL currently displayed vs the URL the customer accepted.
  const state = {
    file: null,
    displayedUrl: null, // output_url currently shown in the preview
    displayedOriginal: null,
    acceptedUrl: null, // output_url injected into the form (== displayedUrl when accepted)
    thumbUrl: null, // object URL of the uploaded photo (for the progress thumbnail)
  };

  // Background-product state (only used when the Worker /config says so).
  const bg = {
    enabled: false,
    backgrounds: [],   // [{id,label,image_url,thumb_url}]
    selectedId: null,
    subjectId: null,   // generation id (server holds the clean cutout)
    subjectW: 0,
    subjectH: 0,
    selected: false, // when true, the resize dots + hint are visible
    placement: { scale: 0.85, xPct: 50, yPct: 50 },
  };

  // ---- Artwork lifecycle broadcast -------------------------------------------
  // Fire and forget. Nothing here requires a listener, and an exception inside
  // one cannot reach this code, so a listener can never break a sale.

  function emit(name, detail) {
    try {
      document.dispatchEvent(new CustomEvent(name, { detail: detail }));
    } catch (e) {
      /* noop */
    }
  }

  function currentBgThumb() {
    const b = bg.backgrounds.find((x) => x.id === bg.selectedId);
    return (b && b.thumb_url) || null;
  }

  // Never call this from pointermove: it must stay out of the drag loop.
  function emitPlacement() {
    if (!bg.enabled || !bg.subjectId) return;
    emit('fsh:artwork-placement', {
      id: bg.subjectId,
      backgroundId: bg.selectedId,
      backgroundThumb: currentBgThumb(),
      placement: Object.assign({}, bg.placement),
    });
  }

  const PROGRESS_MESSAGES = [
    'Mapping your photo…',
    'Adding colors…',
    'Almost there, making it print perfect…',
  ];

  // ---- Cart button locking ---------------------------------------------------

  function getAddToCartButton() {
    const form = document.querySelector('product-form');
    return form ? form.querySelector('.product-form__submit') : null;
  }

  // The Add to Cart button stays visually normal. We intercept the form submit
  // and finish the artwork on the way through, or block it with a hint if there
  // is nothing to finish yet.
  let cartHintEl = null;

  function ensureCartHint() {
    if (cartHintEl) return cartHintEl;
    const btn = getAddToCartButton();
    cartHintEl = document.createElement('p');
    cartHintEl.className = 'fsh-cart-hint';
    cartHintEl.setAttribute('role', 'alert');
    cartHintEl.hidden = true;
    const anchor = (btn && btn.closest('.product-form__buttons')) || btn;
    if (anchor && anchor.parentNode) {
      anchor.parentNode.insertBefore(cartHintEl, anchor.nextSibling);
    } else {
      root.appendChild(cartHintEl);
    }
    return cartHintEl;
  }

  function hideCartHint() {
    if (cartHintEl) cartHintEl.hidden = true;
  }

  // Used for a failure the customer must read where they are — next to the
  // button they just pressed — without dragging them back up the page.
  function showCartHint(message) {
    const hint = ensureCartHint();
    hint.textContent = message;
    hint.hidden = false;
  }

  function gateMessage() {
    if (!state.file) return 'Please upload your photo first.';
    return 'Please tap Create my artwork first.';
  }

  function blockAddToCart() {
    showCartHint(gateMessage());
    root.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  // ---- Hidden line item properties on the product form -----------------------

  function setFormProperty(name, value) {
    const form = document.querySelector('product-form form');
    if (!form) return;
    let input = form.querySelector(`input[name="properties[${name}]"]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${name}]`;
      form.appendChild(input);
    }
    input.value = value;
  }

  function clearFormProperties() {
    const form = document.querySelector('product-form form');
    if (!form) return;
    // CART_NOTE_NAME belongs in this list. It is a claim about the artwork, so
    // it has to die with the artwork: without it, changing photo after accepting
    // one leaves the reassurance behind, and an order could carry a confident
    // sentence about a poster that is not attached.
    const names = [CART_NOTE_NAME, '_print_url', '_original_url', '_preview_url', '_background', '_placement'];
    names.forEach((n) => form.querySelectorAll(`input[name="properties[${n}]"]`).forEach((i) => i.remove()));
  }

  // ---- Acceptance state ------------------------------------------------------

  function resetAcceptance() {
    state.acceptedUrl = null;
    clearFormProperties();
    hideCartHint();
  }

  // Attach the artwork the customer is looking at to the cart form.
  //
  // There is no longer a "Use this artwork" button: Add to Cart is the only
  // step, and the submit gate below calls this on the way through. For a plain
  // product that is safe to do inside the submit event because everything here
  // is synchronous — the image already exists, this only copies its URL into a
  // hidden input, so the very same submit carries it.
  function attachCurrentImage() {
    if (!state.displayedUrl) return false;
    state.acceptedUrl = state.displayedUrl;
    setFormProperty(CART_NOTE_NAME, CART_NOTE);
    setFormProperty('_print_url', state.displayedUrl);
    if (state.displayedOriginal) setFormProperty('_original_url', state.displayedOriginal);
    hideCartHint();
    return true;
  }

  // Background product: composite the subject onto the chosen bg at the current
  // placement (server makes the clean print file), then attach it to the cart.
  //
  // Returns true only when the print file is attached. Unlike the plain path
  // this cannot finish inside a submit event, so the gate blocks the submit,
  // awaits this, and re-submits — which means the answer has to come back as a
  // value rather than as a change of button state.
  async function acceptBg() {
    if (!bg.subjectId) return false;
    try {
      const res = await fetch(`${WORKER_URL}/compose`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: bg.subjectId,
          handle: PRODUCT_HANDLE,
          backgroundId: bg.selectedId,
          scale: bg.placement.scale,
          xPct: bg.placement.xPct,
          yPct: bg.placement.yPct,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status !== 'ok' || !data.output_url) {
        // The customer is looking at the Add to Cart button, not at the preview
        // panel, so the failure has to appear there.
        showCartHint(data.error || 'Could not finalize your artwork. Please try again.');
        return false;
      }
      state.acceptedUrl = data.output_url;
      setFormProperty(CART_NOTE_NAME, CART_NOTE);
      setFormProperty('_print_url', data.output_url);
      if (data.preview_url) setFormProperty('_preview_url', data.preview_url);
      setFormProperty('_background', bg.selectedId || '');
      setFormProperty('_placement', JSON.stringify(bg.placement));
      emit('fsh:artwork-composed', {
        id: bg.subjectId,
        outputUrl: data.output_url,
        previewUrl: data.preview_url || data.output_url,
        backgroundId: bg.selectedId,
        backgroundThumb: currentBgThumb(),
        placement: Object.assign({}, bg.placement),
      });
      hideCartHint();
      return true;
    } catch (e) {
      showCartHint('Network error. Please try again.');
      return false;
    }
  }

  // "Try another image": go back to a fresh upload. Generating again is still
  // rate-limited server-side (cooldown + daily cap per IP), so this can't be
  // abused to burn API cost.
  function tryAnother() {
    resetAcceptance();
    state.displayedUrl = null;
    state.displayedOriginal = null;
    bg.subjectId = null;
    show(els.preview, false);
    show(els.uploadCard, true);
    clearFile();
    setError('');
    root.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  // ---- Generation ------------------------------------------------------------

  function getTurnstileToken() {
    if (!TURNSTILE_KEY || typeof window.turnstile === 'undefined') return '';
    try {
      return window.turnstile.getResponse() || '';
    } catch (e) {
      return '';
    }
  }

  async function generate() {
    if (!state.file) return;
    setError('');
    startProgress(); // hides the upload card, shows the compact progress card

    // Downscale large phone photos before upload: big inputs make uploads slow
    // and Gemini slower, without improving the result.
    const uploadBlob = await downscaleImage(state.file, MAX_UPLOAD_DIM);

    const formData = new FormData();
    formData.append('image', uploadBlob, 'photo.jpg');
    if (PRODUCT_ID) formData.append('productId', PRODUCT_ID);
    if (PRODUCT_HANDLE) formData.append('productHandle', PRODUCT_HANDLE);
    const token = getTurnstileToken();
    if (token) formData.append('turnstileToken', token);

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), GENERATE_TIMEOUT_MS);

    try {
      const res = await fetch(`${WORKER_URL}/generate`, {
        method: 'POST',
        body: formData,
        signal: controller.signal,
      });
      const data = await res.json().catch(() => ({}));

      const okPlain = data.status === 'ok' && data.output_url;
      const okBg = data.status === 'ok' && data.mode === 'background' && data.subject_preview_url;
      if (!res.ok || (!okPlain && !okBg)) {
        stopProgress(false);
        setError(data.error || 'Something went wrong. Please try again.');
        return;
      }

      stopProgress(true); // fills to 100% then hides the progress card
      if (okBg) {
        enterBgPreview(data); // background product: drag/zoom subject on a bg
      } else {
        // Show the watermarked preview; keep the clean URL only for the cart property.
        showArtwork(data.preview_url || data.output_url, data.output_url, data.original_url);
      }
      resetAcceptance(); // new image: not accepted until "Use this artwork"
      emit('fsh:artwork-generated', {
        response: data,
        productId: PRODUCT_ID,
        productHandle: PRODUCT_HANDLE,
        backgroundId: bg.enabled ? bg.selectedId : null,
        backgroundThumb: bg.enabled ? currentBgThumb() : null,
        placement: bg.enabled ? Object.assign({}, bg.placement) : null,
      });
    } catch (e) {
      stopProgress(false);
      if (e.name === 'AbortError') {
        setError('This is taking longer than expected. Please try again.');
      } else {
        setError('Network error. Please check your connection and try again.');
      }
    } finally {
      clearTimeout(timeout);
      resetTurnstile();
    }
  }

  function showArtwork(previewUrl, outputUrl, originalUrl) {
    state.displayedUrl = outputUrl; // clean URL, attached to the cart as _print_url
    state.displayedOriginal = originalUrl || null;
    els.artwork.src = previewUrl || outputUrl; // display the watermarked preview
    show(els.bgstage, false);
    show(els.stage, true);
    show(els.preview, true);
  }

  // ---- Background mode (drag/zoom subject on a background) --------------------

  async function loadBgConfig() {
    if (!PRODUCT_HANDLE) return;
    try {
      const res = await fetch(`${WORKER_URL}/config/${encodeURIComponent(PRODUCT_HANDLE)}`);
      const cfg = await res.json().catch(() => ({}));
      if (cfg.mode !== 'background' || !Array.isArray(cfg.backgrounds) || !cfg.backgrounds.length) return;
      bg.enabled = true;
      bg.backgrounds = cfg.backgrounds;
      bg.selectedId = cfg.backgrounds[0].id;
      renderBgPicker();
    } catch (e) {
      /* stay in plain mode */
    }
  }

  function renderBgPicker() {
    if (!els.bgpicker) return;
    if (bg.backgrounds.length <= 1) { show(els.bgpicker, false); return; }
    els.bgpicker.innerHTML = '';
    bg.backgrounds.forEach((b) => {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'fsh-bgpicker__item' + (b.id === bg.selectedId ? ' is-selected' : '');
      item.title = b.label || '';
      const img = document.createElement('img');
      img.src = b.thumb_url;
      img.alt = b.label || '';
      item.appendChild(img);
      item.addEventListener('click', () => selectBackground(b.id));
      els.bgpicker.appendChild(item);
    });
    show(els.bgpicker, true);
  }

  function selectBackground(id) {
    if (id === bg.selectedId) return;
    bg.selectedId = id;
    const b = bg.backgrounds.find((x) => x.id === id);
    if (b && els.bgstageBg) els.bgstageBg.src = b.image_url;
    if (els.bgpicker) {
      [...els.bgpicker.children].forEach((c, i) =>
        c.classList.toggle('is-selected', bg.backgrounds[i] && bg.backgrounds[i].id === id)
      );
    }
    if (state.acceptedUrl) resetAcceptance(); // new bg invalidates a prior accept
    emitPlacement();
  }

  function enterBgPreview(data) {
    bg.subjectId = data.id;
    bg.subjectW = data.subject_w || 1;
    bg.subjectH = data.subject_h || 1;
    bg.placement = { scale: 0.85, xPct: 50, yPct: 50 };
    state.displayedUrl = data.subject_preview_url; // marker for the cart-gate message
    state.acceptedUrl = null;

    const selected = bg.backgrounds.find((x) => x.id === bg.selectedId) || bg.backgrounds[0];
    if (els.bgstageBg && selected) els.bgstageBg.src = selected.image_url;
    if (els.bgstageSubject) els.bgstageSubject.src = data.subject_preview_url;

    show(els.stage, false);
    show(els.bgstage, true);
    show(els.preview, true);
    setSelected(true); // start selected so the customer sees they can adjust
    requestAnimationFrame(layoutSubject);
  }

  // Selection = whether the resize dots + hint are shown. Customers tap empty
  // space to hide them (clean preview) and tap the subject to bring them back.
  function setSelected(sel) {
    bg.selected = sel;
    show(els.bgstageFrame, sel);
  }

  // Hit-test a point (client coords) against the subject's current box on stage.
  function isInsideSubject(clientX, clientY) {
    if (!els.bgstage || !bg.subjectH) return false;
    const r = els.bgstage.getBoundingClientRect();
    const aspect = bg.subjectW / bg.subjectH || 1;
    const h = bg.placement.scale * r.height;
    const w = h * aspect;
    const cx = (bg.placement.xPct / 100) * r.width;
    const cy = (bg.placement.yPct / 100) * r.height;
    const px = clientX - r.left;
    const py = clientY - r.top;
    return Math.abs(px - cx) <= w / 2 && Math.abs(py - cy) <= h / 2;
  }

  function layoutSubject() {
    if (!els.bgstage || !els.bgstageSubject || !bg.subjectH) return;
    const r = els.bgstage.getBoundingClientRect();
    if (!r.width) return;
    const aspect = bg.subjectW / bg.subjectH;
    const h = bg.placement.scale * r.height;
    const w = h * aspect;
    const left = (bg.placement.xPct / 100) * r.width - w / 2;
    const top = (bg.placement.yPct / 100) * r.height - h / 2;
    const s = els.bgstageSubject.style;
    s.width = w + 'px';
    s.height = h + 'px';
    s.left = left + 'px';
    s.top = top + 'px';
    // Keep the selection frame (and its corner dots) on the subject box.
    if (els.bgstageFrame) {
      const f = els.bgstageFrame.style;
      f.width = w + 'px';
      f.height = h + 'px';
      f.left = left + 'px';
      f.top = top + 'px';
    }
  }

  function setupBgDrag() {
    const stage = els.bgstage;
    if (!stage) return;
    const pointers = new Map();
    let dragStart = null;
    let pinchStart = null;
    let moved = false; // did this gesture move/zoom (vs. a plain tap)?
    let multi = false; // did this gesture ever use 2 fingers (pinch)?
    let wheelSettle = null;
    const dist = (a, b2) => Math.hypot(a.x - b2.x, a.y - b2.y);

    stage.addEventListener('pointerdown', (e) => {
      stage.setPointerCapture(e.pointerId);
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      stage.classList.add('is-dragging');
      if (pointers.size === 1) {
        moved = false;
        multi = false;
        dragStart = { x: e.clientX, y: e.clientY, xPct: bg.placement.xPct, yPct: bg.placement.yPct };
      } else if (pointers.size === 2) {
        multi = true;
        const p = [...pointers.values()];
        pinchStart = { dist: dist(p[0], p[1]), scale: bg.placement.scale };
      }
    });

    stage.addEventListener('pointermove', (e) => {
      if (!pointers.has(e.pointerId)) return;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      const r = stage.getBoundingClientRect();
      if (pointers.size >= 2 && pinchStart) {
        const p = [...pointers.values()];
        bg.placement.scale = clampN(pinchStart.scale * (dist(p[0], p[1]) / pinchStart.dist), 0.2, 3);
      } else if (dragStart) {
        const dx = e.clientX - dragStart.x;
        const dy = e.clientY - dragStart.y;
        // A small jitter is still a tap; only past the threshold is it a drag.
        if (!moved && Math.hypot(dx, dy) > 6) { moved = true; setSelected(true); }
        bg.placement.xPct = clampN(dragStart.xPct + (dx / r.width) * 100, -20, 120);
        bg.placement.yPct = clampN(dragStart.yPct + (dy / r.height) * 100, -20, 120);
      } else {
        return;
      }
      layoutSubject();
      if (state.acceptedUrl) resetAcceptance();
    });

    const endPointer = (e) => {
      pointers.delete(e.pointerId);
      if (pointers.size < 2) pinchStart = null;
      if (pointers.size === 0) {
        // A tap (no drag, no pinch) toggles the dots: show when tapping the
        // subject, hide when tapping empty space so the preview is unobstructed.
        if (!moved && !multi) setSelected(isInsideSubject(e.clientX, e.clientY));
        dragStart = null;
        moved = false;
        multi = false;
        stage.classList.remove('is-dragging');
        emitPlacement();
      }
    };
    stage.addEventListener('pointerup', endPointer);
    stage.addEventListener('pointercancel', endPointer);

    stage.addEventListener('wheel', (e) => {
      e.preventDefault();
      if (!bg.selected) setSelected(true);
      bg.placement.scale = clampN(bg.placement.scale * (1 - e.deltaY * 0.0012), 0.2, 3);
      layoutSubject();
      if (state.acceptedUrl) resetAcceptance();
      // Wheel events arrive in bursts; report once the customer settles.
      clearTimeout(wheelSettle);
      wheelSettle = setTimeout(emitPlacement, 200);
    }, { passive: false });
  }

  // Corner dots: drag a dot to resize the subject. The opposite corner stays
  // anchored (standard resize feel). Aspect ratio is locked, so dragging any
  // corner scales the subject and re-centers the box between the anchor and the
  // dragged corner. All math is in stage pixels, then written back as % placement.
  function setupHandleResize() {
    const stage = els.bgstage;
    const frame = els.bgstageFrame;
    if (!stage || !frame) return;
    const handles = frame.querySelectorAll('[data-fsh-handle]');

    handles.forEach((handle) => {
      let st = null; // active resize session

      handle.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        e.stopPropagation(); // don't let the stage start a move-drag
        handle.setPointerCapture(e.pointerId);

        const r = stage.getBoundingClientRect();
        const aspect = bg.subjectW / bg.subjectH || 1;
        const h = bg.placement.scale * r.height;
        const w = h * aspect;
        const cx = (bg.placement.xPct / 100) * r.width;
        const cy = (bg.placement.yPct / 100) * r.height;
        // Corner sign: x grows right, y grows down, measured from the center.
        const corner = handle.dataset.fshHandle;
        const sx = corner === 'tr' || corner === 'br' ? 1 : -1;
        const sy = corner === 'bl' || corner === 'br' ? 1 : -1;
        st = {
          rect: r,
          aspect,
          sx,
          sy,
          wStart: w,
          hStart: h,
          // Anchor = the opposite corner, fixed in stage pixels for the drag.
          ox: cx - sx * (w / 2),
          oy: cy - sy * (h / 2),
        };
      });

      handle.addEventListener('pointermove', (e) => {
        if (!st) return;
        const px = e.clientX - st.rect.left;
        const py = e.clientY - st.rect.top;
        // Scale by whichever axis the customer pulls more (feels responsive).
        const factor = Math.max(
          Math.abs(px - st.ox) / Math.max(1, st.wStart),
          Math.abs(py - st.oy) / Math.max(1, st.hStart)
        );
        let scale = clampN((st.hStart * factor) / st.rect.height, 0.2, 3);
        const hNew = scale * st.rect.height;
        const wNew = hNew * st.aspect;
        const cxNew = st.ox + st.sx * (wNew / 2);
        const cyNew = st.oy + st.sy * (hNew / 2);
        bg.placement.scale = scale;
        bg.placement.xPct = clampN((cxNew / st.rect.width) * 100, -20, 120);
        bg.placement.yPct = clampN((cyNew / st.rect.height) * 100, -20, 120);
        layoutSubject();
        if (state.acceptedUrl) resetAcceptance();
      });

      const end = (e) => {
        if (!st) return;
        try { handle.releasePointerCapture(e.pointerId); } catch (err) { /* noop */ }
        st = null;
        emitPlacement();
      };
      handle.addEventListener('pointerup', end);
      handle.addEventListener('pointercancel', end);
    });
  }

  function clampN(v, lo, hi) {
    return Math.min(hi, Math.max(lo, v));
  }

  function resetTurnstile() {
    if (TURNSTILE_KEY && typeof window.turnstile !== 'undefined') {
      try { window.turnstile.reset(); } catch (e) { /* noop */ }
    }
  }

  // Resize a large image down to maxDim on its long edge, re-encoded as JPEG.
  // Returns the original file if it's already small enough or on any failure.
  function downscaleImage(file, maxDim) {
    return new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        const longEdge = Math.max(img.width, img.height);
        if (!longEdge || longEdge <= maxDim) {
          resolve(file);
          return;
        }
        const scale = maxDim / longEdge;
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        try {
          canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
          canvas.toBlob((blob) => resolve(blob || file), 'image/jpeg', 0.9);
        } catch (e) {
          resolve(file);
        }
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(file);
      };
      img.src = url;
    });
  }

  // ---- Progress UI -----------------------------------------------------------

  let progressTimer = null;
  let progressMsgTimer = null;
  let progressVal = 0;

  function setProgressValue(p) {
    progressVal = p;
    const v = Math.round(p);
    if (els.progressFill) els.progressFill.style.width = v + '%';
    if (els.progressPercent) els.progressPercent.textContent = v + '%';
  }

  function startProgress() {
    // Swap the upload card for the progress card.
    show(els.uploadCard, false);
    show(els.preview, false);
    show(els.progress, true);

    // Thumbnail of the uploaded photo.
    if (els.progressThumb && state.file) {
      if (state.thumbUrl) URL.revokeObjectURL(state.thumbUrl);
      state.thumbUrl = URL.createObjectURL(state.file);
      els.progressThumb.src = state.thumbUrl;
    }

    // Simulated progress: ease slowly towards 92% and let completion finish it.
    // Gentle ramp so the bar doesn't hit the ceiling long before the result.
    setProgressValue(0);
    progressTimer = setInterval(() => {
      const remaining = 92 - progressVal;
      if (remaining > 0) setProgressValue(progressVal + Math.max(0.3, remaining * 0.03));
    }, 450);

    // Rotating subtitle.
    let i = 0;
    if (els.progressSub) els.progressSub.textContent = PROGRESS_MESSAGES[0];
    progressMsgTimer = setInterval(() => {
      i = (i + 1) % PROGRESS_MESSAGES.length;
      if (els.progressSub) els.progressSub.textContent = PROGRESS_MESSAGES[i];
    }, 4000);
  }

  function stopProgress(success) {
    if (progressTimer) clearInterval(progressTimer);
    if (progressMsgTimer) clearInterval(progressMsgTimer);
    progressTimer = null;
    progressMsgTimer = null;

    if (success) {
      setProgressValue(100);
      // Brief beat at 100% before revealing the result. The upload card stays
      // hidden; the preview card (with "Try another image") replaces it.
      setTimeout(() => show(els.progress, false), 350);
    } else {
      show(els.progress, false);
      show(els.uploadCard, true); // let the customer try again
    }
  }

  // ---- File selection --------------------------------------------------------

  const ALLOWED = ['image/jpeg', 'image/png', 'image/webp'];
  const MAX_BYTES = 10 * 1024 * 1024;

  function handleFile(file) {
    setError('');
    if (!file) return;

    if (!ALLOWED.includes(file.type)) {
      setError('Please upload a JPG, PNG, or WebP image.');
      clearFile();
      return;
    }
    if (file.size > MAX_BYTES) {
      setError('Image is too large (max 10MB).');
      clearFile();
      return;
    }

    state.file = file;
    els.uploadText.textContent = file.name;
    els.generateBtn.disabled = false;

    // A brand-new upload invalidates any previously accepted/preview image.
    state.displayedUrl = null;
    state.displayedOriginal = null;
    resetAcceptance();
    show(els.preview, false);
  }

  function onFileChange() {
    handleFile(els.fileInput.files && els.fileInput.files[0]);
  }

  function clearFile() {
    state.file = null;
    els.fileInput.value = '';
    els.uploadText.textContent = 'Drag & drop your photo';
    els.generateBtn.disabled = true;
  }

  // ---- Small DOM helpers -----------------------------------------------------

  function show(el, visible) {
    if (!el) return;
    el.hidden = !visible;
  }

  function setError(msg) {
    if (!els.error) return;
    els.error.textContent = msg || '';
    els.error.hidden = !msg;
  }

  // ---- Init ------------------------------------------------------------------

  // Add to Cart is the only step. This capture-phase listener runs before the
  // theme's own handler and does whatever the artwork still needs before the
  // line item is allowed through:
  //
  //   nothing generated yet  -> block, and say what is missing
  //   plain product          -> attach the URL and let THIS submit continue,
  //                             because attaching is synchronous
  //   background product     -> block, compose the print file server-side,
  //                             then submit again
  //
  // The invariant is unchanged and still lives here alone: a line item cannot
  // reach the cart unless _print_url has been set from the artwork on screen.
  let finalizing = false;

  document.addEventListener(
    'submit',
    function (event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.closest('product-form')) return;
      if (state.acceptedUrl) return; // print file already attached

      if (!state.displayedUrl) {
        event.preventDefault();
        event.stopImmediatePropagation();
        blockAddToCart();
        return;
      }

      if (!bg.enabled) {
        // Synchronous: the hidden input exists before the theme's handler reads
        // the form, so this submit already carries the print file.
        if (attachCurrentImage()) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        blockAddToCart();
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      finalizeThenSubmit(form);
    },
    true
  );

  // Background products only. Composing takes a few seconds and can fail, so
  // the button has to show it is working — otherwise it reads as a dead tap and
  // the customer taps again.
  async function finalizeThenSubmit(form) {
    if (finalizing) return; // a second tap while the first is still composing
    finalizing = true;
    setCartBusy(true);
    let ok = false;
    try {
      ok = await acceptBg();
    } finally {
      setCartBusy(false);
      finalizing = false;
    }
    // The gate lets this one through: acceptedUrl is set now.
    if (ok) form.requestSubmit();
  }

  // Same busy treatment the theme gives the button on a normal add to cart, so
  // the two phases of the tap look like one action.
  function setCartBusy(on) {
    const btn = getAddToCartButton();
    if (!btn) return;
    btn.classList.toggle('loading', on);
    btn.setAttribute('aria-busy', on ? 'true' : 'false');
    btn.disabled = on;
    const spinner = btn.querySelector('.loading__spinner, .loading-overlay__spinner');
    if (spinner) spinner.classList.toggle('hidden', !on);
  }

  els.fileInput.addEventListener('change', onFileChange);
  els.generateBtn.addEventListener('click', generate);
  if (els.retryBtn) els.retryBtn.addEventListener('click', tryAnother);

  // Background products: load config (enables bg mode + picker) and wire drag/zoom.
  loadBgConfig();
  setupBgDrag();
  setupHandleResize();
  window.addEventListener('resize', () => { if (bg.enabled) layoutSubject(); });

  // Tapping/clicking anywhere outside the stage also hides the dots, so the
  // customer gets a clean preview. Taps inside the stage are handled by the
  // stage's own tap logic (subject = show, background = hide).
  document.addEventListener('pointerdown', (e) => {
    if (!bg.enabled || !bg.selected) return;
    if (els.bgstage && els.bgstage.contains(e.target)) return;
    setSelected(false);
  });

  // Drag & drop support on the upload zone.
  const dropzone = root.querySelector('[data-fsh-dropzone]');
  if (dropzone) {
    ['dragenter', 'dragover'].forEach((ev) =>
      dropzone.addEventListener(ev, (e) => {
        e.preventDefault();
        dropzone.classList.add('fsh-dragover');
      })
    );
    ['dragleave', 'dragend'].forEach((ev) =>
      dropzone.addEventListener(ev, () => dropzone.classList.remove('fsh-dragover'))
    );
    dropzone.addEventListener('drop', (e) => {
      e.preventDefault();
      dropzone.classList.remove('fsh-dragover');
      const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (!file) return;
      try {
        els.fileInput.files = e.dataTransfer.files; // keep the input in sync
      } catch (err) {
        /* some browsers disallow assigning files; state.file is what we send */
      }
      handleFile(file);
    });
  }
})();
